import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { obtenerSesion, cerrarSesion } from '../utils/sesion';
import ModalConfirmacion from '../components/ModalConfirmacion';
import NuevoCobroModal from '../components/NuevoCobroModal';
import ComprobanteCobro from '../components/ComprobanteCobro';
import ReporteCierre from '../components/ReporteCierre';
import {
  formatearMonto,
  formatearHora,
  formatearNumeroComprobante,
  ETIQUETAS_METODO,
  ETIQUETAS_MOTIVO,
} from '../utils/formato';
import { IconDinero, IconAdvertencia, IconExito, IconAgregar, IconImprimir } from '../components/icons';
import '../styles/caja.css';

const SIN_ANULADOS = { cantidad: 0, total: 0 };

function Caja() {
  const navigate = useNavigate();
  const sesion = obtenerSesion();
  const operador = sesion?.operador;

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  const [sesionCaja, setSesionCaja] = useState(null);
  const [cobros, setCobros] = useState([]);
  const [totales, setTotales] = useState([]);
  const [anulados, setAnulados] = useState(SIN_ANULADOS);

  const [montoInicial, setMontoInicial] = useState('');
  const [abriendo, setAbriendo] = useState(false);

  const [mostrarNuevoCobro, setMostrarNuevoCobro] = useState(false);
  const [comprobante, setComprobante] = useState(null); // { cobro, registradoAhora }

  const [cobroAAnular, setCobroAAnular] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [errorAnulacion, setErrorAnulacion] = useState('');

  const [mostrarModalCierre, setMostrarModalCierre] = useState(false);
  const [montoFinal, setMontoFinal] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const [reporteCierre, setReporteCierre] = useState(null);

  // silencioso: recarga los datos sin tapar la pantalla con "Cargando…" (después de registrar o anular).
  const cargarActual = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCargando(true);
    setError('');
    try {
      const { data } = await api.get('/sesiones-caja/actual');
      setSesionCaja(data.sesion_caja);
      setCobros(data.cobros);
      setTotales(data.totales_por_metodo);
      setAnulados(data.anulados || SIN_ANULADOS);
    } catch (err) {
      setError('No se pudo cargar el estado de la caja. Reintentá en unos segundos.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarActual();
  }, [cargarActual]);

  // Al pasar a un comprobante o al reporte de cierre se vuelve al comienzo de la página: si el
  // operador venía scrolleado (en el celular, tocando un botón de la lista), el aviso de arriba
  // quedaría tapado por la barra superior y el comprobante a mitad de pantalla.
  useEffect(() => {
    if (comprobante || reporteCierre) window.scrollTo({ top: 0 });
  }, [comprobante, reporteCierre]);

  function manejarLogout() {
    cerrarSesion();
    navigate('/login', { replace: true });
  }

  async function manejarAbrirCaja(evento) {
    evento.preventDefault();
    setError('');
    setAbriendo(true);
    try {
      await api.post('/sesiones-caja/abrir', { monto_inicial: Number(montoInicial) });
      setMontoInicial('');
      await cargarActual();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo abrir la caja.');
    } finally {
      setAbriendo(false);
    }
  }

  async function manejarCobroRegistrado(cobro) {
    setMostrarNuevoCobro(false);
    setExito('');
    await cargarActual({ silencioso: true });
    setComprobante({ cobro, registradoAhora: true });
  }

  function abrirAnulacion(cobro) {
    setCobroAAnular(cobro);
    setMotivoAnulacion('');
    setErrorAnulacion('');
  }

  async function manejarConfirmarAnulacion() {
    setAnulando(true);
    setErrorAnulacion('');
    try {
      await api.post(`/cobros/${cobroAAnular.id}/anular`, { motivo: motivoAnulacion.trim() });
      setExito(
        `El cobro N° ${formatearNumeroComprobante(cobroAAnular.numero_comprobante)} quedó anulado. ` +
          'El original sigue registrado, marcado como anulado.'
      );
      setCobroAAnular(null);
      await cargarActual({ silencioso: true });
    } catch (err) {
      setErrorAnulacion(err.response?.data?.error || 'No se pudo anular el cobro. Reintentá.');
    } finally {
      setAnulando(false);
    }
  }

  async function manejarConfirmarCierre() {
    setCerrando(true);
    setError('');
    try {
      const { data } = await api.post(`/sesiones-caja/${sesionCaja.id}/cerrar`, {
        monto_final_declarado: Number(montoFinal),
      });
      setSesionCaja(data.sesion_caja);
      setReporteCierre(data.reporte_cierre);
      setMostrarModalCierre(false);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cerrar la caja.');
      setMostrarModalCierre(false);
    } finally {
      setCerrando(false);
    }
  }

  async function manejarVolverDespuesDeCierre() {
    setReporteCierre(null);
    setMontoFinal('');
    await cargarActual();
  }

  const totalGeneral = totales.reduce((acumulado, t) => acumulado + Number(t.total), 0);
  const cantidadVigentes = totales.reduce((acumulado, t) => acumulado + Number(t.cantidad), 0);

  return (
    <div className="caja-container">
      <header className="caja-navbar no-imprimir">
        <div className="caja-navbar-marca">
          <img src="/logo-betech.svg" alt="Betech" className="caja-navbar-logo" />
          <div>
            <p className="caja-navbar-titulo">Caja</p>
            {operador && (
              <p className="caja-navbar-operador">
                {operador.usuario} · {operador.municipio_id}
              </p>
            )}
          </div>
        </div>
        <div className="caja-navbar-acciones">
          <Link to="/historial" className="btn-navbar">
            Historial de cierres
          </Link>
          <Link to="/cambiar-password" className="btn-navbar">
            Cambiar contraseña
          </Link>
          <button type="button" className="btn-navbar" onClick={manejarLogout}>
            Cerrar sesión
          </button>
        </div>
      </header>

      <div className="caja-content">
        {operador?.debe_cambiar_password && (
          <div className="aviso-banner no-imprimir">
            <IconAdvertencia size={18} />
            Por seguridad, tenés que cambiar tu contraseña. <Link to="/cambiar-password">Cambiarla ahora</Link>
          </div>
        )}

        {error && (
          <p className="mensaje-error no-imprimir">
            <IconAdvertencia size={16} />
            {error}
          </p>
        )}

        {exito && !comprobante && !reporteCierre && (
          <p className="mensaje-exito no-imprimir" role="status">
            <IconExito size={16} />
            {exito}
          </p>
        )}

        {cargando && !comprobante && !reporteCierre && <p>Cargando…</p>}

        {comprobante && (
          <ComprobanteCobro
            cobro={comprobante.cobro}
            operador={operador}
            registradoAhora={comprobante.registradoAhora}
            onVolver={() => setComprobante(null)}
            textoVolver={reporteCierre ? 'Volver al cierre' : 'Volver a Caja'}
          />
        )}

        {!comprobante && !cargando && !sesionCaja && !reporteCierre && (
          <section className="tarjeta">
            <h2>No tenés una caja abierta</h2>
            <p>Contá el efectivo con el que arrancás el turno y abrí tu caja para empezar a cobrar.</p>
            <form onSubmit={manejarAbrirCaja} className="formulario-apertura">
              <div className="form-group">
                <label htmlFor="monto_inicial">Monto inicial en efectivo</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <IconDinero size={18} />
                  </span>
                  <input
                    id="monto_inicial"
                    className="form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={montoInicial}
                    onChange={(e) => setMontoInicial(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="boton boton-primario" disabled={abriendo}>
                {abriendo ? 'Abriendo…' : 'Abrir caja'}
              </button>
            </form>
          </section>
        )}

        {!comprobante && !cargando && sesionCaja && !reporteCierre && (
          <>
            <section className="tarjeta">
              <h2>Turno actual</h2>
              <div className="resumen-grilla">
                <div>
                  <span className="etiqueta">Abierta desde</span>
                  <span>{formatearHora(sesionCaja.fecha_apertura)}</span>
                </div>
                <div>
                  <span className="etiqueta">Monto inicial</span>
                  <span>{formatearMonto(sesionCaja.monto_inicial)}</span>
                </div>
                <div>
                  <span className="etiqueta">Cobrado en el turno</span>
                  <span>{formatearMonto(totalGeneral)}</span>
                </div>
                <div>
                  <span className="etiqueta">Cantidad de cobros</span>
                  <span>{cantidadVigentes}</span>
                </div>
              </div>

              {(totales.length > 0 || anulados.cantidad > 0) && (
                <div className="totales-por-metodo">
                  {totales.map((t) => (
                    <span key={t.metodo_pago} className="chip-total">
                      {ETIQUETAS_METODO[t.metodo_pago] || t.metodo_pago}: {formatearMonto(t.total)} ({t.cantidad})
                    </span>
                  ))}
                  {anulados.cantidad > 0 && (
                    <span className="chip-total chip-anulado">
                      Anulados: {formatearMonto(anulados.total)} ({anulados.cantidad})
                    </span>
                  )}
                </div>
              )}

              <div className="acciones-turno">
                <button
                  type="button"
                  className="boton boton-primario boton-con-icono"
                  onClick={() => {
                    setExito('');
                    setMostrarNuevoCobro(true);
                  }}
                >
                  <IconAgregar size={18} />
                  Nuevo cobro
                </button>
                <button type="button" className="boton boton-secundario" onClick={() => setMostrarModalCierre(true)}>
                  Cerrar caja
                </button>
              </div>
            </section>

            <section className="tarjeta">
              <h2>Cobros del turno</h2>
              {cobros.length === 0 ? (
                <p>Todavía no registraste ningún cobro en este turno. Tocá «Nuevo cobro» para cargar el primero.</p>
              ) : (
                <div className="tabla-cobros-wrapper">
                  <table className="tabla-cobros tabla-movimientos">
                    <thead>
                      <tr>
                        <th>N°</th>
                        <th>Hora</th>
                        <th>Contribuyente</th>
                        <th>Trámite</th>
                        <th>Método</th>
                        <th>Monto</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cobros.map((c) => (
                        <tr key={c.id} className={c.anulacion ? 'fila-anulada' : ''}>
                          <td data-label="N°">#{formatearNumeroComprobante(c.numero_comprobante)}</td>
                          <td data-label="Hora">{formatearHora(c.fecha_hora)}</td>
                          <td data-label="Contribuyente" className="celda-flexible">{c.contribuyente_nombre}</td>
                          <td data-label="Trámite">{ETIQUETAS_MOTIVO[c.motivo] || c.motivo}</td>
                          <td data-label="Método" className="celda-flexible">{ETIQUETAS_METODO[c.metodo_pago] || c.metodo_pago}</td>
                          <td data-label="Monto" className="celda-monto">{formatearMonto(c.monto)}</td>
                          <td data-label="Estado">
                            {c.anulacion ? (
                              <span className="badge-anulado" title={`Motivo: ${c.anulacion.motivo}`}>
                                Anulado
                              </span>
                            ) : (
                              <span className="badge-vigente">Vigente</span>
                            )}
                          </td>
                          <td className="celda-acciones">
                            <div className="acciones-fila">
                              <button
                                type="button"
                                className="boton boton-chico boton-secundario boton-con-icono"
                                onClick={() => setComprobante({ cobro: c, registradoAhora: false })}
                              >
                                <IconImprimir size={14} />
                                Comprobante
                              </button>
                              {!c.anulacion && (
                                <button
                                  type="button"
                                  className="boton boton-chico boton-peligro-suave"
                                  onClick={() => abrirAnulacion(c)}
                                >
                                  Anular
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {!comprobante && reporteCierre && (
          <ReporteCierre
            sesionCaja={sesionCaja}
            reporteCierre={reporteCierre}
            operador={operador}
            cobros={cobros}
            onVerComprobante={(cobro) => setComprobante({ cobro, registradoAhora: false })}
            onVolver={manejarVolverDespuesDeCierre}
          />
        )}
      </div>

      {mostrarNuevoCobro && (
        <NuevoCobroModal onCerrar={() => setMostrarNuevoCobro(false)} onRegistrado={manejarCobroRegistrado} />
      )}

      {cobroAAnular && (
        <ModalConfirmacion
          titulo="Anular cobro"
          textoConfirmar="Anular cobro"
          peligro
          confirmando={anulando}
          confirmarDeshabilitado={motivoAnulacion.trim().length < 5}
          onCancelar={() => setCobroAAnular(null)}
          onConfirmar={manejarConfirmarAnulacion}
        >
          <dl className="resumen-cobro">
            <div>
              <dt>Cobro</dt>
              <dd>N° {formatearNumeroComprobante(cobroAAnular.numero_comprobante)}</dd>
            </div>
            <div>
              <dt>Contribuyente</dt>
              <dd>{cobroAAnular.contribuyente_nombre}</dd>
            </div>
            <div>
              <dt>Monto</dt>
              <dd>
                {formatearMonto(cobroAAnular.monto)} · {ETIQUETAS_METODO[cobroAAnular.metodo_pago]}
              </dd>
            </div>
          </dl>
          <p className="modal-ayuda">
            El cobro original no se borra: queda registrado como anulado y deja de sumar en los totales de la caja. Esto no
            se puede deshacer.
          </p>
          {errorAnulacion && (
            <p className="mensaje-error">
              <IconAdvertencia size={16} />
              {errorAnulacion}
            </p>
          )}
          <div className="form-group">
            <label htmlFor="motivo_anulacion">Motivo de la anulación</label>
            <input
              id="motivo_anulacion"
              type="text"
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && motivoAnulacion.trim().length >= 5 && !anulando) manejarConfirmarAnulacion();
              }}
              maxLength={255}
              placeholder="Ej.: monto mal cargado"
              autoComplete="off"
              autoFocus
            />
          </div>
        </ModalConfirmacion>
      )}

      {mostrarModalCierre && (
        <ModalConfirmacion
          titulo="Cerrar caja"
          textoConfirmar="Confirmar cierre"
          confirmando={cerrando}
          onCancelar={() => setMostrarModalCierre(false)}
          onConfirmar={manejarConfirmarCierre}
        >
          <p>Contá el efectivo real que tenés y escribí el monto. Esta acción no se puede deshacer.</p>
          <div className="form-group">
            <label htmlFor="monto_final">Efectivo contado</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <IconDinero size={18} />
              </span>
              <input
                id="monto_final"
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                value={montoFinal}
                onChange={(e) => setMontoFinal(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        </ModalConfirmacion>
      )}
    </div>
  );
}

export default Caja;
